import { cn } from '../../lib/cn.ts';

export type RunOutcome = 'passed' | 'failed' | 'running' | 'cancelled';

const TICK: Record<RunOutcome, string> = {
  passed: 'bg-fg-subtle opacity-60',
  failed: 'bg-status-danger-fill',
  running: 'bg-status-working-fill',
  cancelled: 'bg-border-strong',
};

export interface RunStripProps {
  /** Oldest first. */
  runs: readonly RunOutcome[];
  /** Slots shown; missing history draws as empty slots on the left. */
  slots?: number;
  /** Accessible summary: "Last 20 runs: 16 passed, 4 failed". */
  label: string;
  /**
   * How a passed run draws: success green (default, the owner: a pass reads as a pass everywhere,
   * the same as the rows beside it), or quiet grey where only failures should stand out.
   */
  passed?: 'quiet' | 'success';
  className?: string;
}

/**
 * Recent runs as a row of ticks, newest on the right: the shape of a history at a glance. Green
 * passed (grey with `passed="quiet"`), red failed, cyan running, grey cancelled, so every page
 * reads an outcome by the same colour.
 */
export function RunStrip({ runs, slots = 24, label, passed = 'success', className }: RunStripProps) {
  const shown = runs.slice(-slots);
  const empty = Math.max(0, slots - shown.length);
  return (
    <span role="img" aria-label={label} className={cn('flex h-5 min-w-0 items-stretch gap-0.5', className)}>
      {Array.from({ length: empty }, (_, i) => (
        <span key={`e${i}`} aria-hidden className="min-w-0 flex-1 rounded-pill bg-active" />
      ))}
      {shown.map((r, i) => (
        <span key={i} aria-hidden data-run={r} className={cn('motion-grow min-w-0 flex-1 rounded-pill', r === 'passed' && passed === 'success' ? 'bg-status-success-fill' : TICK[r])} style={{ transitionDelay: `${(empty + i) * 18}ms` }} />
      ))}
    </span>
  );
}
